// Is a saved result today's: the words for what the server works out when
// it is read (services/workflows/artifacts/freshness.py, `freshness` on
// every saved result). One vocabulary for the Saved results list's State,
// a saved result's Expected by and the Published page's Reachable rows:
//
//   Fresh · Due 08:30 · Late · Not saved yet · No time set ·
//   Needs a timed schedule · Saved by another workflow, and Stopped beside.
//
// Pure; the self-test is artifactModel.selftest.ts.

import type { ArtifactFreshness } from "@/api/workflowArtifacts";

import { zoneWords } from "../deliveries/timingModel";

export type StateTone = "success" | "warning" | "neutral";

export interface StateWord {
  text: string;
  tone: StateTone;
  /** A tooltip: when it is expected, why it stopped. */
  title?: string;
}

/** The server's pattern: a 24-hour HH:MM. */
export const EXPECTED_BY_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
export const EXPECTED_BY_PROBLEM = "A 24-hour time, like 08:30.";

/**
 * A typed time as the server takes it: "8:30", "0830" and "08.30" are
 * "08:30"; "" (and "None") clears it; null when it is not a time.
 */
export function expectedByFrom(raw: string): string | null {
  const t = raw.trim();
  if (!t || t.toLowerCase() === "none") return "";
  const m = /^(\d{1,2})[:.h]?(\d{2})$/.exec(t);
  if (!m) return null;
  const out = `${m[1].padStart(2, "0")}:${m[2]}`;
  return EXPECTED_BY_RE.test(out) ? out : null;
}

/** "Expected by 08:30 London time" (the schedule's own zone, when the server named it). */
export function expectedWords(f: Pick<ArtifactFreshness, "expected_by" | "tz">): string {
  if (!f.expected_by) return "";
  return `Expected by ${f.expected_by}${f.tz ? ` ${zoneWords(f.tz)}` : ""}`;
}

export interface StateInput {
  /** Some version has been saved. */
  published: boolean;
  freshness?: ArtifactFreshness | null;
  /** A key's read grant was given while another workflow saved it (Published only). */
  moved?: boolean;
}

/**
 * The State word. `unsaved` says "Not saved yet" for a name nothing has
 * saved (Published, which has no Newest column; the Saved results list
 * says it under Newest already). Late outranks it: nothing saved by the
 * time it was expected is late.
 */
export function savedResultState({ published, freshness, moved }: StateInput, { unsaved = false }: { unsaved?: boolean } = {}): StateWord {
  if (moved)
    return {
      text: "Saved by another workflow",
      tone: "warning",
      title: "Another workflow saves it now: keys given it before read nothing.",
    };
  const f: ArtifactFreshness = freshness ?? { state: "unscheduled" };
  const expected = expectedWords(f);
  if (f.state === "late") return { text: "Late", tone: "warning", title: expected || undefined };
  if (unsaved && !published)
    return {
      text: "Not saved yet",
      tone: "neutral",
      title: expected || undefined,
    };
  if (f.state === "fresh") return { text: "Fresh", tone: "success", title: expected || undefined };
  if (f.state === "due")
    return {
      text: f.expected_by ? `Due ${f.expected_by}` : "Due",
      tone: "neutral",
      title: expected || undefined,
    };
  // unscheduled: no time set, or a time with no timed schedule to measure it by
  if (f.expected_by) return { text: "Needs a timed schedule", tone: "warning", title: expected };
  return { text: "No time set", tone: "neutral" };
}

const STOPPED: Record<string, string> = {
  step_changed: "Its step changed after the workflow was last saved. Save the workflow to save it again.",
  not_declared: "No workflow saves it now.",
};

/** "Stopped", shown beside the state, or null. */
export function stoppedWord(f: Pick<ArtifactFreshness, "stopped"> | null | undefined): StateWord | null {
  const why = f?.stopped;
  if (!why) return null;
  return { text: "Stopped", tone: "warning", title: STOPPED[why] ?? undefined };
}
